import { MATTER_FIELD_GROUPS, type MatterFieldGroup } from '@common/constants/matterFields';
import type { ApsOcrEnumOption, ApsOcrExtraction } from '@common/types/apsOcr';
import { formatExtractedFieldValue } from '@common/utils/apsOcrPrefill';
import {
    APS_MATTER_FIELDS,
    getMatterField,
    getMatterFieldApsKey,
    getMatterFieldOptions,
    getMatterFieldWidget,
    type MatterFieldWidget,
} from '@common/utils/matterFields';

export interface DisplayRow {
    label: string;
    value: string;
}

export interface ExtraExtractedField {
    /** Matter field key. */
    key: string;
    label: string;
    /** Value found by the scan. */
    value: string;
    wide?: boolean;
    widget: MatterFieldWidget;
    /** Shown as a select instead of the widget. */
    options?: ApsOcrEnumOption[];
}

export interface ExtraExtractedFieldGroup {
    title: MatterFieldGroup;
    fields: ExtraExtractedField[];
}

const SUMMARY_FIELD_KEYS: (keyof ApsOcrExtraction)[] = [
    'purchaserNames',
    'sellerNames',
    'propertyAddress',
    'purchasePrice',
    'closingDate',
];

export function getDisplayRows(fields: ApsOcrExtraction): DisplayRow[] {
    return SUMMARY_FIELD_KEYS.flatMap((key) => {
        const value = formatExtractedFieldValue(fields, key);
        const label = getMatterField(key)?.label;
        if (!value || !label) return [];
        return [{ label, value }];
    });
}

export function getExtraExtractedFieldGroups(fields: ApsOcrExtraction): ExtraExtractedFieldGroup[] {
    return MATTER_FIELD_GROUPS.map((title) => ({
        title,
        fields: APS_MATTER_FIELDS.filter((field) => field.group === title).flatMap((field) => {
            const apsKey = getMatterFieldApsKey(field);
            if (!apsKey) return [];
            return [
                {
                    key: field.key,
                    label: field.label,
                    value: formatExtractedFieldValue(fields, apsKey),
                    wide: field.wide,
                    widget: getMatterFieldWidget(field),
                    options: getMatterFieldOptions(field),
                },
            ];
        }),
    })).filter((group) => group.fields.length > 0);
}
